import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  loginWithEmail,
  loginWithGoogle,
  loginWithPassword,
  sendOTPCode,
  verifyOTPCode,
  requestPasswordReset,
} from '../services/api';
import ThemeToggle from '../components/ThemeToggle';
import AuthLayout from '../components/AuthLayout';
import { useToast } from '../components/ToastContainer';
import ToastContainer from '../components/ToastContainer';
import { t, getLanguage } from '../utils/i18n';
import { FaLock, FaEnvelope, FaKey } from 'react-icons/fa';
import AuthCard from '../components/auth/AuthCard';
import PasswordLoginForm from '../components/auth/PasswordLoginForm';
import OtpLoginForm from '../components/auth/OtpLoginForm';
import MagicLinkForm from '../components/auth/MagicLinkForm';
import ForgotPasswordModal from '../components/auth/ForgotPasswordModal';

export default function Login() {
  const [activeTab, setActiveTab] = useState('password');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [forgotModalOpen, setForgotModalOpen] = useState(false);
  const [, setLang] = useState(getLanguage());
  const navigate = useNavigate();
  const location = useLocation();
  const { showToast, removeToast, toasts } = useToast();
  const redirectFromQuery = new URLSearchParams(location.search).get('redirect');
  const redirectAfterLogin = location.state?.redirect || (redirectFromQuery && redirectFromQuery.startsWith('/') ? redirectFromQuery : undefined);

  useEffect(() => {
    const handleLanguageChange = () => setLang(getLanguage());
    window.addEventListener('languageChanged', handleLanguageChange);
    return () => window.removeEventListener('languageChanged', handleLanguageChange);
  }, []);

  const getDestination = (user) => {
    if (user?.profileComplete === false) return '/complete-profile';
    return redirectAfterLogin || '/dashboard';
  };

  const handlePasswordLogin = async (email, password) => {
    setIsLoading(true);
    setError('');
    try {
      const result = await loginWithPassword(email, password);
      if (result.success) {
        showToast(t('auth.login.loginSuccess'), 'success', 2000);
        const destination = getDestination(result.user);
        setTimeout(() => navigate(destination, { replace: true }), 500);
      } else {
        setError(result.error || t('auth.login.invalidCredentials'));
        showToast(result.error || t('auth.login.invalidCredentials'), 'error', 6000);
      }
    } catch (err) {
      const msg = err.message || t('auth.login.invalidCredentials');
      setError(msg);
      showToast(msg, 'error', 6000);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOtpSend = async (email) => {
    const result = await sendOTPCode(email);
    if (result.success) {
      showToast(t('auth.login.otpSent'), 'success', 6000);
    }
    return result;
  };

  const handleOtpVerify = async (email, code) => {
    const result = await verifyOTPCode(email, code);
    if (result.success) {
      showToast(t('auth.login.loginSuccess'), 'success', 2000);
      const destination = result.user?.profileComplete === false ? '/complete-profile' : (redirectAfterLogin || '/dashboard');
      setTimeout(() => navigate(destination, { replace: true }), 500);
    }
    return result;
  };

  const handleMagicLink = async (email) => {
    const result = await loginWithEmail(email);
    if (result.success) {
      showToast(t('auth.login.checkEmail'), 'success', 6000);
    }
    return result;
  };

  const handleForgotPassword = async (email) => {
    return requestPasswordReset(email);
  };

  const handleGoogleSignIn = async () => {
    const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;
    const isPlaceholder =
      !clientId ||
      clientId === 'YOUR_GOOGLE_CLIENT_ID' ||
      clientId.toLowerCase() === 'your-google-client-id' ||
      clientId.toLowerCase().includes('your-google') ||
      !clientId.includes('.apps.googleusercontent.com');

    if (isPlaceholder) {
      showToast(t('auth.login.googleNotConfigured'), 'info', 6000);
      return;
    }

    try {
      if (!window.google) {
        const script = document.createElement('script');
        script.src = 'https://accounts.google.com/gsi/client';
        script.async = true;
        script.defer = true;
        document.body.appendChild(script);
        await new Promise((resolve, reject) => {
          script.onload = resolve;
          script.onerror = reject;
          setTimeout(() => reject(new Error('Google script load timeout')), 10000);
        });
      }

      window.google.accounts.id.initialize({
        client_id: clientId,
        callback: async (response) => {
          try {
            const payload = JSON.parse(atob(response.credential.split('.')[1]));
            const idToken = response.credential;
            const email = payload.email;
            const displayName = payload.name;
            const photoUrl = payload.picture;

            const result = await loginWithGoogle(idToken, '', email, displayName, photoUrl);
            if (result.success) {
              showToast(t('auth.login.loginSuccess'), 'success', 2000);
              const destination = result.user?.profileComplete === false ? '/complete-profile' : (redirectAfterLogin || '/dashboard');
              setTimeout(() => navigate(destination, { replace: true }), 500);
            } else {
              showToast(result.error || t('auth.login.googleError'), 'error', 6000);
            }
          } catch (error) {
            showToast(t('auth.login.googleError'), 'error', 6000);
            console.error('Google Sign In callback error:', error);
          }
        },
      });

      const buttonContainer = document.createElement('div');
      buttonContainer.style.position = 'absolute';
      buttonContainer.style.left = '-9999px';
      buttonContainer.style.visibility = 'hidden';
      document.body.appendChild(buttonContainer);

      window.google.accounts.id.renderButton(buttonContainer, {
        type: 'standard',
        theme: 'outline',
        size: 'large',
        text: 'signin_with',
      });

      setTimeout(() => {
        const googleButton = buttonContainer.querySelector('div[role="button"]');
        if (googleButton) {
          googleButton.click();
        } else {
          window.google.accounts.id.prompt((notification) => {
            if (notification.isNotDisplayed()) {
              const reason = notification.getNotDisplayedReason();
              if (reason === 'browser_not_supported' || reason === 'invalid_client') {
                showToast(t('auth.login.googleNotAvailable'), 'info', 6000);
              } else {
                showToast(t('auth.login.googleRequiresHTTPS'), 'info', 6000);
              }
            }
          });
        }
      }, 300);
    } catch (error) {
      showToast(t('auth.login.googleNotAvailable'), 'error', 6000);
      console.error('Google Sign In error:', error);
    }
  };

  const methodLabels = {
    password: { title: t('auth.login.methods.password.title') || 'Prijava lozinkom', desc: t('auth.login.methods.password.desc') || 'Unesite podatke za prijavu.' },
    otp: { title: t('auth.login.methods.otp.title') || 'Prijava jednokratnim kodom', desc: t('auth.login.methods.otp.desc') || 'Unesite email, poslaćemo vam kod.' },
    magic: { title: t('auth.login.methods.magic.title') || 'Prijava linkom', desc: t('auth.login.methods.magic.desc') || 'Unesite email, poslaćemo vam link za prijavu.' },
  };
  const otherMethods = [
    { id: 'password', label: t('auth.login.methods.password.short') || 'Lozinka', icon: FaLock },
    { id: 'otp', label: t('auth.login.methods.otp.short') || 'Kod na email', icon: FaKey },
    { id: 'magic', label: t('auth.login.methods.magic.short') || 'Link na email', icon: FaEnvelope },
  ].filter((m) => m.id !== activeTab);

  return (
    <AuthLayout title="Prijava" subtitle="Prijavite se da biste zakazali čuvanje ili upravljali profilom">
      <ToastContainer toasts={toasts} removeToast={removeToast} />
      <div className="absolute top-4 right-4 z-20">
        <ThemeToggle />
      </div>

      <AuthCard
        title="Prijava"
        subtitle="Unesite podatke i prijavite se."
        onGoogle={handleGoogleSignIn}
      >
        <div role="tabpanel" id={`panel-${activeTab}`}>
          <div className="mb-4">
            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">{methodLabels[activeTab]?.title}</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-0.5">{methodLabels[activeTab]?.desc}</p>
          </div>

          {activeTab === 'password' && (
            <PasswordLoginForm
              onSubmit={handlePasswordLogin}
              onForgotPassword={() => setForgotModalOpen(true)}
              isLoading={isLoading}
              error={error}
              setError={setError}
            />
          )}
          {activeTab === 'otp' && (
            <OtpLoginForm
              onSendCode={handleOtpSend}
              onVerifyCode={handleOtpVerify}
              onSuccess={() => {}}
              setError={setError}
            />
          )}
          {activeTab === 'magic' && <MagicLinkForm onSendLink={handleMagicLink} setError={setError} />}

          {otherMethods.length > 0 && (
            <>
              <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                <div className="flex justify-center mb-4">
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    {t('auth.login.otherMethods') || 'Drugi načini prijave'}
                  </span>
                </div>
                <div className="flex flex-col sm:flex-row gap-2 justify-center">
                  {otherMethods.map((m) => {
                    const Icon = m.icon;
                    return (
                      <button
                        key={m.id}
                        type="button"
                        onClick={() => { setError(''); setActiveTab(m.id); }}
                        className="flex-1 min-w-0 px-4 py-2.5 text-sm font-medium rounded-xl border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 bg-transparent hover:bg-gray-50 dark:hover:bg-gray-700/50 hover:border-gray-400 dark:hover:border-gray-500 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 flex items-center justify-center gap-2"
                      >
                        {Icon && <Icon className="w-4 h-4 flex-shrink-0" />}
                        {m.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            </>
          )}
        </div>
      </AuthCard>

      <ForgotPasswordModal
        isOpen={forgotModalOpen}
        onClose={() => setForgotModalOpen(false)}
        requestPasswordReset={handleForgotPassword}
      />
    </AuthLayout>
  );
}
